<template>
  <div class="h-100">
    <div class="text-center" v-if="isLoading">
      <b-spinner variant="secondary" type="grow" />
    </div>
    <div v-else-if="!isLoading && textContent" class="d-flex justify-content-center w-100">
      <b-button v-if="!hasSelectedWords" variant="outline-primary" @click="showModal" class="mt-3">
        {{ trans("courses.word-hunter.start_advanced_reflection") }}
      </b-button>
      <WordHunterResultsContainer
        v-else
        :selectedTopicId="collectedData.selectedTopicId"
        :topicList="topicList"
        :selectedWords="initialData.selectedWords"
      />
    </div>
    <b-modal
      :id="`${promptId}-words`"
      size="lg"
      scrollable
      hide-footer
      centered
      no-close-on-esc
      no-close-on-backdrop
      @hide="onHideModal"
    >
      <word-selector
        v-if="currentStep === 1"
        :textContent="textContent"
        :selectedWords="collectedData.selectedWords"
        :currentStep="currentStep"
        @selectWord="selectWord"
        @unSelectWord="unSelectWord"
        @next="nextStep"
        :trackingData="trackingData"
      />
      <word-topic-match
        v-if="currentStep === 2"
        :selectedWords="collectedData.selectedWords"
        :currentStep="currentStep"
        :selectedTopicId="collectedData.selectedTopicId"
        :topicList="topicList"
        @next="nextStep"
        @prev="prevStep"
        @selectTopic="selectTopic"
        :trackingData="trackingData"
      />
      <word-mood-match
        v-if="currentStep === 3"
        :currentStep="currentStep"
        :selectedWords="collectedData.selectedWords"
        @next="submitWordHunter"
        @prev="prevStep"
        @selectMood="selectMood"
        :trackingData="trackingData"
        :promptId="promptId"
        :block-id="blockId"
        :block-type="blockType"
      />
      <conclusion-step
        v-if="currentStep === 4"
        :prompt-id="promptId"
        @hideModal="hideModal"
        :trackingData="trackingData"
      />
    </b-modal>

    <b-modal :id="`${promptId}-reflectionInfo`" size="lg" scrollable hide-footer centered>
      <reflection-info
        @hideModalAndStartRef="hideModalAndStartRef"
        @hideModalAndCancleRef="hideModalAndCancleRef"
      />
    </b-modal>
  </div>
</template>

<script>
import WordSelector from "./WordSelector";
import WordTopicMatch from "./WordTopicMatch";
import WordMoodMatch from "./WordMoodMatch";
import ConclusionStep from "./ConclusionStep";
import ReflectionInfo from "./ReflectionInfo";
import WordHunterResultsContainer from "./WordHunterResultsContainer";

export default {
  name: "WordHunter",
  components: {
    WordSelector,
    WordTopicMatch,
    WordMoodMatch,
    ConclusionStep,
    ReflectionInfo,
    WordHunterResultsContainer,
  },
  props: {
    blockId: Number,
    blockType: String,
    promptId: String,
    textContent: String,
    topicId: Number,
    streamId: String,
    aleId: String,
    elementId: String,
    userId: Number,
  },
  data() {
    return {
      isLoading: true,
      currentStep: 1,
      topicList: [],
      collectedData: {
        selectedWords: [],
        selectedTopicId: null,
      },
      initialData: {
        selectedWords: [],
        selectedTopicId: null,
      },
    };
  },
  mounted() {
    this.$axios
      .get(`/api/rest/wordhunter/exercise/${this.promptId}/${this.topicId}`)
      .then((res) => {
        this.isLoading = false;
        this.topicList = res.data.exercise.topicList ? res.data.exercise.topicList.topics : [];
        this.collectedData.selectedWords = res.data.exercise.selectedWords
          ? res.data.exercise.selectedWords
          : [];
        this.collectedData.selectedTopicId = res.data.exercise.selectedTopic
          ? res.data.exercise.selectedTopic.topicId
          : null;
        this.initialData.selectedWords = res.data.exercise.selectedWords
          ? [...res.data.exercise.selectedWords]
          : [];
        this.initialData.selectedTopicId = res.data.exercise.selectedTopic
          ? res.data.exercise.selectedTopic.topicId
          : null;
      })
      .catch((err) => {
        this.isLoading = false;
        console.log(err);
      });
  },
  computed: {
    trackingData() {
      return {
        promptId: this.promptId,
        streamId: this.streamId,
        aleId: this.aleId,
        miuId: this.elementId,
      };
    },
    hasSelectedWords() {
      const wordsLength = this.collectedData.selectedWords.length > 0;
      this.$emit("hasSelectedWords", wordsLength);
      return wordsLength;
    },
    wordsModal() {
      return `${this.promptId}-words`;
    },
    reflectionModal() {
      return `${this.promptId}-reflectionInfo`;
    },
  },
  methods: {
    onHideModal() {
      this.collectedData.selectedWords = [...this.initialData.selectedWords];
      this.collectedData.selectedTopicId = this.initialData.selectedTopicId;
    },
    selectedMood(id) {
      if (id) {
        const mood = this.moodsList.moods.filter((mood) => {
          if (id === mood.id) {
            return mood;
          }
        });
        return mood[0].name;
      }
      return "";
    },
    showModal() {
      this.currentStep = 1;
      if (localStorage.getItem("advancedReflectionInfoHide") === null) {
        this.$bvModal.show(this.reflectionModal);
      } else {
        this.$bvModal.show(this.wordsModal);
      }

      trackEvent("WordhunterStart", this.trackingData);
    },
    hideModal() {
      this.$bvModal.hide(this.wordsModal);
    },
    hideModalAndStartRef() {
      this.$bvModal.hide(this.reflectionModal);
      this.$bvModal.show(this.wordsModal);
    },
    hideModalAndCancleRef() {
      this.$bvModal.hide(this.reflectionModal);
    },
    nextStep() {
      this.currentStep++;
    },
    prevStep() {
      this.currentStep--;
    },
    selectTopic(topic) {
      this.collectedData.selectedTopicId = topic;
    },
    unSelectWord(index) {
      this.collectedData.selectedWords.splice(index, 1);
    },
    selectWord(word) {
      if (word.length > 0) {
        this.collectedData.selectedWords.push({ mood_id: null, word: word });
      }
    },
    selectMood(mood, index) {
      this.collectedData.selectedWords[index].mood_id = mood.id;
    },
    submitWordHunter() {
      const data = {
        promptId: this.promptId,
        selectedTopic: {
          topicId: this.collectedData.selectedTopicId,
          topicListId: this.topicId,
        },
        promptId: this.promptId,
        selectedWords: this.collectedData.selectedWords,
      };

      this.$axios
        .put(`/api/rest/wordhunter/save`, data)
        .then((res) => {
          this.initialData.selectedWords = this.collectedData.selectedWords;
          this.initialData.selectedTopicId = this.collectedData.selectedTopicId;
          this.nextStep();
        })
        .catch((err) => {
          console.log(err);
        });
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep {
  .modal-body {
    padding-top: 0;
  }
}
</style>
